import { createDrawer } from "../ui/drawer";
import { SEVERITY_ORDER, SEVERITY_LABELS, type GwSeverity } from "./audit-data";
import type { AssistantContext } from "./sections";
import type { FindingsSummaryDto, AiMessageDto } from "./ai-api";

export interface AiAssistantDeps {
  // 実モード時に注入。findings サマリ + 会話履歴を渡し AI 応答テキストを得る（実 Bedrock）。
  readonly analyze?: (
    summary: FindingsSummaryDto,
    messages: readonly AiMessageDto[],
  ) => Promise<string>;
}

export interface AiAssistantHandle {
  readonly element: HTMLElement;
  open(context?: AssistantContext, summary?: FindingsSummaryDto): void;
  close(): void;
  isOpen(): boolean;
}

// モック表示用の固定文言（Google Workspace 文脈）。実 AI は analyze 注入時に有効化。
interface MockTopFinding {
  readonly reason: string;
  readonly action: string;
}
interface MockAnalysis {
  readonly summary: string;
  readonly topFindings: readonly MockTopFinding[];
}
const AI_ANALYSIS: MockAnalysis = {
  summary:
    "Google Workspace のヘルススコアは Google 推奨のセキュリティ基準を下回っています。最優先は管理者アカウントの保護（2 段階認証プロセスの未登録）と、ドライブ／共有ドライブの外部共有の制限です。これらは認証情報の窃取や情報漏洩に直結します。次点でサードパーティ OAuth アプリの過剰スコープと、休眠アカウントの棚卸しを是正してください。",
  topFindings: [
    { reason: "管理者の 2 段階認証プロセス（2SV）が未登録。フィッシング・認証情報窃取の最大経路。", action: "全特権管理者に 2SV（可能ならセキュリティキー）を必須化する。" },
    { reason: "ドライブの外部共有が無制限。意図しない情報露出のリスク。", action: "共有設定を許可ドメインに限定し、リンク共有の既定を制限する。" },
    { reason: "サードパーティ OAuth アプリに広範なスコープを許可。トークン漏洩時の影響が甚大。", action: "API 制御でアプリを承認制にし、過剰スコープのアプリを制限する。" },
  ],
};

const AI_SUGGESTED_PROMPTS: readonly string[] = [
  "重大(critical)の検出だけ優先度順に説明して",
  "管理者の 2 段階認証プロセス(2SV)を全社展開する手順を教えて",
  "ドライブの外部共有リスクを最小化する設定変更案を出して",
  "サードパーティ OAuth アプリの過剰スコープへの対処を教えて",
];

const SUMMARY_PROMPT = "この診断結果の総合評価を簡潔に述べてください。";
const FAIL_MESSAGE = "AI 分析に失敗しました。時間をおいて再度お試しください。";
const SAMPLE_NOTE = "応答はサンプルです。実 AI（AWS Bedrock）連携は後工程。";
const TOP_LIMIT = 5;

function mockReply(prompt: string): string {
  return (
    "（サンプル応答）ご質問「" +
    prompt +
    "」について、現状の検出結果では重大(critical)の項目から優先対応することを推奨します。" +
    "実際の AI 分析（AWS Bedrock）連携は後工程で有効化されます。"
  );
}

function sevRank(s: string): number {
  return SEVERITY_ORDER[s as GwSeverity] ?? 9;
}
function sevLabel(s: string): string {
  return SEVERITY_LABELS[s as GwSeverity] ?? s;
}

export function createGwAiAssistant(deps?: AiAssistantDeps): AiAssistantHandle {
  const drawer = createDrawer({ title: "AIアシスタント" });
  const analyzeFn = deps?.analyze;
  // 実モードの会話履歴（analyze に role/text のみ渡す）。
  const chat: AiMessageDto[] = [];
  // 現在の findings サマリ（実モードの文脈）。実/モックは「その open 呼び出しで summary が渡されたか」
  // だけで決まる（spec: summary 無し open → モック）。古い summary は保持しない。
  let currentSummary: FindingsSummaryDto | null = null;

  const container = document.createElement("div");
  container.className = "space-y-4";

  // 総合評価
  const summarySec = document.createElement("section");
  summarySec.className = "rounded-lg border border-slate-200 bg-slate-50 p-3";
  const sumTitle = document.createElement("h3");
  sumTitle.className = "text-sm font-medium text-slate-700";
  sumTitle.textContent = "組織の総合評価";
  const sumBody = document.createElement("p");
  sumBody.className = "mt-1 text-sm text-slate-700";
  sumBody.textContent = AI_ANALYSIS.summary;
  summarySec.append(sumTitle, sumBody);

  // 文脈ノート(検出からの相談時に表示)
  const contextNote = document.createElement("p");
  contextNote.className = "hidden text-sm text-shinonome-700";
  contextNote.dataset["testid"] = "ai-context-note";

  // 優先指摘
  const top = document.createElement("section");
  const topTitle = document.createElement("h3");
  topTitle.className = "text-sm font-medium text-slate-700";
  topTitle.textContent = "優先指摘";
  const topList = document.createElement("ul");
  topList.className = "mt-1 space-y-1 text-sm text-slate-700 list-disc pl-5";
  top.append(topTitle, topList);

  // おすすめプロンプト
  const sugg = document.createElement("div");
  sugg.className = "flex flex-wrap gap-2";
  for (const p of AI_SUGGESTED_PROMPTS) {
    const chip = document.createElement("button");
    chip.type = "button";
    chip.dataset["testid"] = "suggested-prompt";
    chip.className = "rounded-full border border-slate-300 px-3 py-1 text-xs text-slate-700 hover:bg-slate-100";
    chip.textContent = p;
    chip.addEventListener("click", () => {
      input.value = p;
      input.focus();
    });
    sugg.append(chip);
  }

  // チャットログ
  const log = document.createElement("div");
  log.className = "space-y-2";
  log.setAttribute("aria-live", "polite");

  function appendMessageEl(role: "user" | "assistant", text: string): HTMLElement {
    const row = document.createElement("div");
    row.dataset["testid"] = "ai-message";
    row.className =
      role === "user"
        ? "ml-8 rounded-lg bg-shinonome-50 px-3 py-2 text-sm text-slate-900"
        : "mr-8 rounded-lg bg-slate-100 px-3 py-2 text-sm text-slate-800";
    row.textContent = text;
    log.append(row);
    return row;
  }

  // 入力フォーム
  const form = document.createElement("form");
  form.className = "flex gap-2";
  const inputLabel = document.createElement("label");
  inputLabel.className = "sr-only";
  inputLabel.htmlFor = "ai-input";
  inputLabel.textContent = "AIへの質問";
  const input = document.createElement("input");
  input.id = "ai-input";
  input.type = "text";
  input.dataset["testid"] = "ai-input";
  input.placeholder = "追加の調査や対応策を質問…";
  input.className = "flex-1 rounded border border-slate-300 px-3 py-2 text-sm";
  const send = document.createElement("button");
  send.type = "submit";
  send.className = "rounded bg-shinonome-700 px-3 py-2 text-sm font-medium text-white hover:bg-shinonome-800";
  send.textContent = "送信";
  form.append(inputLabel, input, send);

  const note = document.createElement("p");
  note.className = "text-xs text-slate-400";
  note.textContent = SAMPLE_NOTE;

  function isLive(): boolean {
    return analyzeFn !== undefined && currentSummary !== null;
  }

  // 優先指摘: 実モードは summary.failing を severity 降順で表示（非 AI・決定的）。モックは AI_ANALYSIS。
  function renderTopFindings(): void {
    topList.replaceChildren();
    if (isLive() && currentSummary !== null) {
      const sorted = [...currentSummary.failing]
        .sort((a, b) => sevRank(a.severity) - sevRank(b.severity))
        .slice(0, TOP_LIMIT);
      if (sorted.length === 0) {
        const li = document.createElement("li");
        li.textContent = "要対応の検出はありません。";
        topList.append(li);
        return;
      }
      for (const f of sorted) {
        const li = document.createElement("li");
        li.textContent = "[" + sevLabel(f.severity) + "] " + f.title;
        topList.append(li);
      }
      return;
    }
    for (const t of AI_ANALYSIS.topFindings) {
      const li = document.createElement("li");
      li.textContent = t.reason + " → " + t.action;
      topList.append(li);
    }
  }

  form.addEventListener("submit", (e) => {
    e.preventDefault();
    const text = input.value.trim();
    if (text === "") return;
    input.value = "";
    if (analyzeFn !== undefined && currentSummary !== null) {
      // 実モード: 会話履歴を analyze に渡し、応答を表示。
      chat.push({ role: "user", text });
      appendMessageEl("user", text);
      const pending = appendMessageEl("assistant", "分析中…");
      const history: AiMessageDto[] = chat.map((m) => ({ role: m.role, text: m.text }));
      void analyzeFn(currentSummary, history)
        .then((reply) => {
          pending.textContent = reply;
          chat.push({ role: "assistant", text: reply });
        })
        .catch(() => {
          pending.textContent = FAIL_MESSAGE;
        });
      return;
    }
    // モック
    appendMessageEl("user", text);
    appendMessageEl("assistant", mockReply(text));
  });

  container.append(summarySec, contextNote, top, sugg, log, form, note);
  drawer.setContent(container);

  // 実モードの総合評価（open 時 1 回）。
  function runSummaryAnalysis(): void {
    if (analyzeFn === undefined || currentSummary === null) return;
    sumBody.textContent = "分析中…";
    void analyzeFn(currentSummary, [{ role: "user", text: SUMMARY_PROMPT }])
      .then((text) => {
        sumBody.textContent = text;
      })
      .catch(() => {
        sumBody.textContent = FAIL_MESSAGE;
      });
  }

  return {
    element: drawer.element,
    open(context?: AssistantContext, summary?: FindingsSummaryDto) {
      // 実/モックは当該 open の summary だけで決まる。保持しない＝ヘッダの汎用起動（summary 無し）は
      // 常にモック。古い summary の再利用や、サンプル表示中の実 Bedrock 誤送信を防ぐ（mock honesty）。
      currentSummary = summary ?? null;
      if (context) {
        contextNote.textContent = context.title + " について相談中";
        contextNote.classList.remove("hidden");
        input.value = context.prompt;
      } else {
        contextNote.classList.add("hidden");
      }
      if (isLive()) {
        // 実モードでは「サンプル」注記を消す（hidden クラスだけだと textContent に残るため text もクリア）。
        note.classList.add("hidden");
        note.textContent = "";
        renderTopFindings();
        runSummaryAnalysis();
      } else {
        note.classList.remove("hidden");
        note.textContent = SAMPLE_NOTE;
        sumBody.textContent = AI_ANALYSIS.summary;
        renderTopFindings();
      }
      drawer.open();
    },
    close: () => drawer.close(),
    isOpen: () => drawer.isOpen(),
  };
}
